/**
 * Récapitulatif des salles occupées et effectifs par jour.
 * Affiche le nombre de salles et d'étudiants par jour d'examen.
 * Permet de rendre une salle inactive directement.
 * Affiche les alertes de capacité et permet de ré-adapter le brassage.
 */

import React, { useState, useRef } from 'react';
import {
  Loader2, AlertCircle, Building2, Users, ToggleLeft, ToggleRight,
  Printer, ChevronDown, ChevronUp, RefreshCw, AlertTriangle, XCircle,
  Info, CheckCircle, Zap,
} from 'lucide-react';
import {
  useGetRecapSallesQuery,
  useToggleSalleActifMutation,
  useReadapterBrassageMutation,
  RecapJour,
  RecapSalleGlobal,
  AlerteCapacite,
  ActionSuggeree,
} from '../../services/emploisTempsApi';

interface Props {
  sessionId: number;
  isDark: boolean;
}

const FONT_OPTIONS = [
  { value: "'Calibri', 'Segoe UI', sans-serif", label: 'Calibri' },
  { value: "'Arial', sans-serif", label: 'Arial' },
  { value: "'Times New Roman', serif", label: 'Times New Roman' },
  { value: "'Georgia', serif", label: 'Georgia' },
  { value: "'Verdana', sans-serif", label: 'Verdana' },
  { value: "'Trebuchet MS', sans-serif", label: 'Trebuchet MS' },
  { value: "'Tahoma', sans-serif", label: 'Tahoma' },
  { value: "'Courier New', monospace", label: 'Courier New' },
];

const RecapSalles: React.FC<Props> = ({ sessionId, isDark }) => {
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [showGlobal, setShowGlobal] = useState(false);
  const [readaptResult, setReadaptResult] = useState<{ success: boolean; message: string } | null>(null);
  const [fontFamily, setFontFamily] = useState(FONT_OPTIONS[0].value);
  const [fontScale, setFontScale] = useState(100);
  const printRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error, refetch } = useGetRecapSallesQuery(sessionId);
  const [toggleSalle] = useToggleSalleActifMutation();
  const [readapterBrassage, { isLoading: isReadapting }] = useReadapterBrassageMutation();

  const handleToggleSalle = async (salleId: number) => {
    try {
      await toggleSalle(salleId).unwrap();
      refetch();
    } catch (err) {
      console.error(err);
    }
  };

  const handleReadapterBrassage = async () => {
    try {
      const result = await readapterBrassage(sessionId).unwrap();
      setReadaptResult({
        success: true,
        message: result.message + (
          result.etudiants_non_places > 0
            ? ` ⚠ ${result.etudiants_non_places} étudiant(s) non placé(s).`
            : ''
        ),
      });
      refetch();
    } catch (err: any) {
      setReadaptResult({
        success: false,
        message: err?.data?.error || "Erreur lors de la réadaptation du brassage.",
      });
    }
  };

  const handleReactiverSalle = async (salleId: number) => {
    try {
      await toggleSalle(salleId).unwrap();
      refetch();
    } catch (err) {
      console.error(err);
    }
  };

  const handlePrint = () => {
    if (!data) return;
    const win = window.open('', '_blank');
    if (!win) return;

    const NAVY = '#1A2E4A';
    const GOLD = '#FFD700';
    const s = (basePt: number) => `${(basePt * fontScale / 100).toFixed(1)}pt`;

    // Générer le HTML complet avec toutes les données dépliées
    let joursHtml = '';

    if (showGlobal) {
      // Vue globale par site
      const sites: Record<string, typeof data.toutes_salles> = {};
      for (const s of data.toutes_salles) {
        if (!sites[s.site_nom]) sites[s.site_nom] = [];
        sites[s.site_nom].push(s);
      }

      Object.entries(sites).forEach(([siteNom, sallesList]) => {
        const totalCap = sallesList.reduce((sum, s) => sum + s.capacite, 0);
        const totalEff = sallesList.reduce((sum, s) => sum + s.effectif, 0);
        const tauxSite = totalCap > 0 ? Math.round(totalEff / totalCap * 100) : 0;

        joursHtml += `<div style="margin-bottom:16px;">
          <div style="background:${NAVY};color:#fff;padding:8px 12px;border-radius:5px 5px 0 0;font-weight:700;font-size:${s(10)};display:flex;justify-content:space-between;align-items:center;">
            <span>${siteNom}</span>
            <span style="font-size:${s(8)};color:${GOLD};">${sallesList.length} salles · ${totalEff}/${totalCap} places · ${tauxSite}%</span>
          </div>
          <table>
            <thead><tr>
              <th style="text-align:left;">Salle</th>
              <th>Capacité</th>
              <th>Effectif</th>
              <th>L1</th><th>L2</th><th>L3</th>
              <th>Statut</th>
            </tr></thead>
            <tbody>
              ${sallesList.map(s => `<tr${!s.salle_actif ? ' style="color:#999;text-decoration:line-through;"' : ''}>
                <td style="text-align:left;font-weight:600;">${s.salle_nom}</td>
                <td>${s.capacite}</td>
                <td style="font-weight:600;">${s.effectif}</td>
                <td>${s.par_niveau?.L1 || '-'}</td>
                <td>${s.par_niveau?.L2 || '-'}</td>
                <td>${s.par_niveau?.L3 || '-'}</td>
                <td>${s.salle_actif ? '<span style="color:green;">Active</span>' : '<span style="color:red;">Inactive</span>'}</td>
              </tr>`).join('')}
            </tbody>
            <tfoot><tr style="font-weight:700;border-top:2px solid #999;">
              <td style="text-align:left;">Total</td>
              <td>${totalCap}</td>
              <td>${totalEff}</td>
              <td>${sallesList.reduce((sum, s) => sum + (s.par_niveau?.L1 || 0), 0) || '-'}</td>
              <td>${sallesList.reduce((sum, s) => sum + (s.par_niveau?.L2 || 0), 0) || '-'}</td>
              <td>${sallesList.reduce((sum, s) => sum + (s.par_niveau?.L3 || 0), 0) || '-'}</td>
              <td></td>
            </tr></tfoot>
          </table>
        </div>`;
      });
    } else {
      // Vue par jour
      data.jours.forEach((jour) => {
        const occColor = jour.taux_occupation_global > 90 ? '#dc2626' : jour.taux_occupation_global > 70 ? '#ea580c' : '#16a34a';
        const niveauxBadges = jour.niveaux.map(n => {
          const bg = n === 'L1' ? '#dbeafe' : n === 'L2' ? '#dcfce7' : '#f3e8ff';
          const fg = n === 'L1' ? '#1d4ed8' : n === 'L2' ? '#15803d' : '#7e22ce';
          return `<span style="background:${bg};color:${fg};padding:2px 8px;border-radius:3px;font-size:${s(8)};font-weight:600;margin-right:4px;">${n}</span>`;
        }).join('');

        const polesBadges = (jour.poles || []).map(p => {
          const isP = p.toLowerCase().includes('poly');
          const isC = p.toLowerCase().includes('com');
          const isJ = p.toLowerCase().includes('jur');
          const bg = isP ? '#FEF3C7' : isC ? '#CCFBF1' : isJ ? '#FFE4E6' : '#F3F4F6';
          const fg = isP ? '#92400E' : isC ? '#0F766E' : isJ ? '#BE123C' : '#374151';
          return `<span style="background:${bg};color:${fg};padding:2px 8px;border-radius:3px;font-size:${s(8)};font-weight:600;margin-right:4px;">${p}</span>`;
        }).join('');

        // Résumé effectifs par site
        const siteSummaryHtml = (jour.par_site || []).map(site => {
          const sc = site.taux_occupation > 90 ? '#dc2626' : site.taux_occupation > 70 ? '#ea580c' : '#16a34a';
          return `<span style="background:#EFF6FF;border:1px solid #BFDBFE;padding:3px 10px;border-radius:4px;font-size:${s(8)};margin-right:6px;">
            <b>${site.site_nom}</b>: <span style="color:${sc};font-weight:700;">${site.effectif}</span> / ${site.capacite} (${site.nombre_salles} salles)
          </span>`;
        }).join('');

        // Grouper salles par site
        const sallesParSite: Record<string, typeof jour.salles> = {};
        for (const sl of jour.salles) {
          if (!sallesParSite[sl.site_nom]) sallesParSite[sl.site_nom] = [];
          sallesParSite[sl.site_nom].push(sl);
        }

        let tbodyHtml = '';
        Object.entries(sallesParSite).forEach(([siteNom, salles]) => {
          const siteCap = salles.reduce((sum, r) => sum + r.capacite, 0);
          const siteEff = salles.reduce((sum, r) => sum + r.effectif, 0);
          const siteTaux = siteCap > 0 ? Math.round(siteEff / siteCap * 1000) / 10 : 0;
          const stColor = siteTaux > 90 ? '#dc2626' : siteTaux > 70 ? '#ea580c' : '#16a34a';

          tbodyHtml += `<tr style="background:#EFF6FF;">
            <td colspan="5" style="text-align:left;font-weight:700;font-size:${s(8.5)};border-bottom:2px solid #93C5FD;padding:5px 8px;">
              ${siteNom} — <span style="font-weight:400;">${salles.length} salles</span>
              <span style="float:right;color:${stColor};font-weight:700;">Effectif: ${siteEff} / ${siteCap} (${siteTaux}%)</span>
            </td>
          </tr>`;

          salles.forEach(sl => {
            const sColor = sl.taux_occupation > 90 ? '#dc2626' : sl.taux_occupation > 70 ? '#ea580c' : '#16a34a';
            tbodyHtml += `<tr${!sl.salle_actif ? ' style="color:#999;text-decoration:line-through;"' : ''}>
              <td style="text-align:left;padding-left:20px;font-weight:600;">${sl.salle_nom}</td>
              <td>${sl.capacite}</td>
              <td style="font-weight:600;">${sl.effectif}</td>
              <td><span style="color:${sColor};font-weight:600;">${sl.taux_occupation}%</span></td>
              <td>${sl.salle_actif ? '<span style="color:green;">Active</span>' : '<span style="color:red;">Inactive</span>'}</td>
            </tr>`;
          });
        });

        joursHtml += `<div style="margin-bottom:16px;">
          <div style="background:${NAVY};color:#fff;padding:8px 12px;border-radius:5px 5px 0 0;display:flex;justify-content:space-between;align-items:center;">
            <div>
              <span style="font-weight:700;font-size:${s(10)};color:${GOLD};">${jour.jour_semaine}</span>
              <span style="font-size:${s(9)};margin-left:8px;color:#ccc;">${jour.date_label}</span>
              <span style="margin-left:12px;">${niveauxBadges}</span>
              <span style="margin-left:8px;">${polesBadges}</span>
            </div>
            <div style="display:flex;align-items:center;gap:16px;">
              <span style="font-size:${s(8)};">${jour.nombre_salles} salles · ${jour.total_etudiants}/${jour.total_capacite} places</span>
              <span style="font-weight:700;font-size:${s(12)};color:${occColor};">${jour.taux_occupation_global}%</span>
            </div>
          </div>
          <div style="padding:6px 12px;background:#FAFAFA;border-left:1px solid #ccc;border-right:1px solid #ccc;">
            ${siteSummaryHtml}
          </div>
          <table>
            <thead><tr>
              <th style="text-align:left;">Salle</th>
              <th>Capacité</th>
              <th>Effectif</th>
              <th>Taux</th>
              <th>Statut</th>
            </tr></thead>
            <tbody>
              ${tbodyHtml}
            </tbody>
            <tfoot><tr style="font-weight:700;border-top:2px solid #999;">
              <td style="text-align:left;">Total général</td>
              <td>${jour.total_capacite}</td>
              <td>${jour.total_etudiants}</td>
              <td><span style="color:${occColor};font-weight:700;">${jour.taux_occupation_global}%</span></td>
              <td></td>
            </tr></tfoot>
          </table>
        </div>`;
      });
    }

    // Résumé global en haut
    const summaryHtml = `<div style="display:flex;justify-content:center;gap:40px;margin-bottom:16px;padding:10px;border:2px solid ${GOLD};border-radius:6px;background:#FFFDE7;">
      <div style="text-align:center;"><span style="font-size:${s(16)};font-weight:700;color:${NAVY};">${data.nombre_total_salles}</span><br/><span style="font-size:${s(8)};color:#666;">Salles</span></div>
      <div style="text-align:center;"><span style="font-size:${s(16)};font-weight:700;color:${NAVY};">${data.nombre_total_sites}</span><br/><span style="font-size:${s(8)};color:#666;">Sites</span></div>
      <div style="text-align:center;"><span style="font-size:${s(16)};font-weight:700;color:${NAVY};">${data.nombre_total_etudiants}</span><br/><span style="font-size:${s(8)};color:#666;">Étudiants</span></div>
      <div style="text-align:center;"><span style="font-size:${s(16)};font-weight:700;color:${NAVY};">${data.jours.length}</span><br/><span style="font-size:${s(8)};color:#666;">Jours d'examen</span></div>
      ${data.etudiants_non_places > 0 ? `<div style="text-align:center;"><span style="font-size:${s(16)};font-weight:700;color:#dc2626;">${data.etudiants_non_places}</span><br/><span style="font-size:${s(8)};color:#dc2626;">Non placés</span></div>` : ''}
    </div>`;

    const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

    win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Récapitulatif Salles</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
  body { font-family: ${fontFamily}; font-size: ${s(9)}; color: #1a1a1a; background: #fff; }
  table { width: 100%; border-collapse: collapse; }
  th { background: ${NAVY}; color: ${GOLD}; padding: 6px 8px; border: 1px solid ${NAVY}; font-size: ${s(8)}; text-transform: uppercase; font-weight: 700; }
  td { border: 1px solid #ccc; padding: 5px 8px; text-align: center; font-size: ${s(8.5)}; }
  tbody tr:nth-child(even) td { background: #F5F7FA; }
</style></head><body>
  <div style="text-align:center;margin-bottom:6px;">
    <div style="font-size:${s(14)};font-weight:700;color:${NAVY};text-transform:uppercase;letter-spacing:1px;">Récapitulatif des salles d'examen</div>
    <div style="font-size:${s(9)};color:#666;margin-top:4px;">${showGlobal ? 'Vue globale par site' : 'Vue par jour'}</div>
  </div>
  ${summaryHtml}
  ${joursHtml}
  <div style="margin-top:16px;text-align:center;font-size:${s(8)};color:#999;border-top:1px solid #ddd;padding-top:6px;">
    Document généré le ${today} — Système de Gestion des Notes
  </div>
</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`flex flex-col items-center justify-center gap-2 py-16 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        <AlertCircle size={24} />
        <span>Aucun brassage trouvé pour cette session.</span>
        <span className="text-sm">Lancez d'abord le brassage depuis l'onglet Configuration.</span>
      </div>
    );
  }

  const hasAlertes = data.alertes && data.alertes.length > 0;
  const hasActions = data.actions_suggerees && data.actions_suggerees.length > 0;

  return (
    <div className="space-y-6">
      {/* Contrôles */}
      <div className={`flex flex-wrap gap-4 items-center p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center gap-3">
          <Building2 size={20} className="text-blue-500" />
          <div>
            <div className="font-semibold text-sm">{data.nombre_total_salles} salles</div>
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{data.nombre_total_sites} sites</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Users size={20} className="text-green-500" />
          <div>
            <div className="font-semibold text-sm">{data.nombre_total_etudiants} étudiants</div>
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{data.jours.length} jours d'examens</div>
          </div>
        </div>

        {data.etudiants_non_places > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium">
            <AlertTriangle size={16} />
            {data.etudiants_non_places} non placé(s)
          </div>
        )}

        <label className="flex items-center gap-2 cursor-pointer ml-4">
          <input
            type="checkbox"
            checked={showGlobal}
            onChange={(e) => setShowGlobal(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded"
          />
          <span className="text-sm">Vue globale des salles</span>
        </label>

        <div>
          <label className="block text-xs font-medium mb-1">Police</label>
          <select
            value={fontFamily}
            onChange={(e) => setFontFamily(e.target.value)}
            className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
          >
            {FONT_OPTIONS.map((f) => (
              <option key={f.label} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1">Taille ({fontScale}%)</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={70}
              max={150}
              step={5}
              value={fontScale}
              onChange={(e) => setFontScale(Number(e.target.value))}
              className="w-24"
            />
            <button
              onClick={() => setFontScale(100)}
              className={`text-xs px-2 py-1 rounded border ${isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'}`}
            >
              Reset
            </button>
          </div>
        </div>

        <div className="flex-1" />

        {/* Bouton Réadapter le brassage */}
        {data.brassage_obsolete && (
          <button
            onClick={handleReadapterBrassage}
            disabled={isReadapting}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
              isReadapting
                ? 'bg-orange-300 text-orange-800 cursor-wait'
                : 'bg-orange-500 text-white hover:bg-orange-600 shadow-md hover:shadow-lg'
            }`}
          >
            {isReadapting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <RefreshCw size={16} />
            )}
            {isReadapting ? 'Réadaptation en cours...' : 'Mettre à jour le brassage'}
          </button>
        )}

        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          <Printer size={16} />
          Imprimer
        </button>
      </div>

      {/* Résultat de réadaptation */}
      {readaptResult && (
        <div className={`flex items-start gap-3 p-4 rounded-xl border ${
          readaptResult.success
            ? (isDark ? 'bg-green-900/20 border-green-700 text-green-300' : 'bg-green-50 border-green-200 text-green-800')
            : (isDark ? 'bg-red-900/20 border-red-700 text-red-300' : 'bg-red-50 border-red-200 text-red-800')
        }`}>
          {readaptResult.success ? <CheckCircle size={20} /> : <XCircle size={20} />}
          <div className="flex-1">
            <div className="font-medium text-sm">
              {readaptResult.success ? 'Brassage réadapté avec succès' : 'Erreur de réadaptation'}
            </div>
            <div className="text-sm mt-1">{readaptResult.message}</div>
          </div>
          <button
            onClick={() => setReadaptResult(null)}
            className="p-1 hover:opacity-70"
          >
            <XCircle size={16} />
          </button>
        </div>
      )}

      {/* Alertes de capacité */}
      {hasAlertes && (
        <div className="space-y-3">
          {data.alertes.map((alerte, idx) => (
            <AlerteBanner key={idx} alerte={alerte} isDark={isDark} />
          ))}
        </div>
      )}

      {/* Actions suggérées */}
      {hasActions && (
        <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center gap-2 mb-3">
            <Zap size={18} className="text-amber-500" />
            <span className={`font-semibold text-sm ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
              Actions suggérées
            </span>
          </div>
          <div className="space-y-2">
            {data.actions_suggerees.map((action, idx) => (
              <ActionSuggereeCard
                key={idx}
                action={action}
                isDark={isDark}
                onReactiverSalle={handleReactiverSalle}
              />
            ))}
          </div>
        </div>
      )}

      {/* Contenu imprimable */}
      <div ref={printRef}>
        <div className="title" style={{ display: 'none' }}>RÉCAPITULATIF DES SALLES D'EXAMEN</div>

        {/* Récap par jour */}
        {!showGlobal && data.jours.map((jour) => (
          <JourRecap
            key={jour.date}
            jour={jour}
            isDark={isDark}
            expanded={expandedDay === jour.date}
            onToggleExpand={() => setExpandedDay(expandedDay === jour.date ? null : jour.date)}
            onToggleSalle={handleToggleSalle}
          />
        ))}

        {/* Vue globale */}
        {showGlobal && (
          <GlobalRecap
            salles={data.toutes_salles}
            isDark={isDark}
            onToggleSalle={handleToggleSalle}
          />
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Composant Alerte
// ============================================================================

const AlerteBanner: React.FC<{ alerte: AlerteCapacite; isDark: boolean }> = ({ alerte, isDark }) => {
  const config = {
    error: {
      bg: isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200',
      text: isDark ? 'text-red-300' : 'text-red-800',
      icon: <XCircle size={20} className="text-red-500" />,
    },
    warning: {
      bg: isDark ? 'bg-orange-900/20 border-orange-700' : 'bg-orange-50 border-orange-200',
      text: isDark ? 'text-orange-300' : 'text-orange-800',
      icon: <AlertTriangle size={20} className="text-orange-500" />,
    },
    info: {
      bg: isDark ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200',
      text: isDark ? 'text-blue-300' : 'text-blue-800',
      icon: <Info size={20} className="text-blue-500" />,
    },
  }[alerte.type];

  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border ${config.bg} ${config.text}`}>
      {config.icon}
      <div>
        <div className="font-semibold text-sm">{alerte.titre}</div>
        <div className="text-sm mt-0.5">{alerte.message}</div>
      </div>
    </div>
  );
};

// ============================================================================
// Composant Action Suggérée
// ============================================================================

const ActionSuggereeCard: React.FC<{
  action: ActionSuggeree;
  isDark: boolean;
  onReactiverSalle: (salleId: number) => void;
}> = ({ action, isDark, onReactiverSalle }) => {
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${
      isDark ? 'bg-gray-750 border-gray-600' : 'bg-white border-gray-200'
    }`}>
      <div className="flex-1">
        <div className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
          {action.label}
        </div>
        <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {action.description}
        </div>
      </div>
      {action.type === 'reactiver_salle' && action.salle_id && (
        <button
          onClick={() => onReactiverSalle(action.salle_id!)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition ml-3"
        >
          <ToggleRight size={14} />
          Réactiver
        </button>
      )}
      {action.type === 'alternance_l2_l3' && (
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ml-3 ${
          isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'
        }`}>
          <Info size={14} />
          Modifier les contraintes
        </div>
      )}
      {action.type === 'alternance_poles' && (
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ml-3 ${
          isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'
        }`}>
          <Info size={14} />
          Modifier les contraintes
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Composant Jour
// ============================================================================

interface JourRecapProps {
  jour: RecapJour;
  isDark: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleSalle: (id: number) => void;
}

const JourRecap: React.FC<JourRecapProps> = ({ jour, isDark, expanded, onToggleExpand, onToggleSalle }) => {
  const occupationColor = jour.taux_occupation_global > 90
    ? 'text-red-500' : jour.taux_occupation_global > 70
      ? 'text-orange-500' : 'text-green-500';

  // Grouper les salles par site
  const sallesParSite: Record<string, typeof jour.salles> = {};
  for (const s of jour.salles) {
    if (!sallesParSite[s.site_nom]) sallesParSite[s.site_nom] = [];
    sallesParSite[s.site_nom].push(s);
  }

  return (
    <div className={`rounded-xl border mb-4 overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* En-tête jour */}
      <button
        onClick={onToggleExpand}
        className={`w-full flex items-center justify-between p-4 text-left hover:bg-opacity-80 transition ${isDark ? 'bg-gray-750' : 'bg-orange-50'}`}
      >
        <div className="flex items-center gap-4">
          <div>
            <span className="font-bold text-blue-600">{jour.jour_semaine}</span>
            <span className={`ml-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{jour.date_label}</span>
          </div>
          <div className={`flex gap-1`}>
            {jour.niveaux.map(n => (
              <span key={n} className={`px-2 py-0.5 rounded text-xs font-medium ${
                n === 'L1' ? 'bg-blue-100 text-blue-700' :
                n === 'L2' ? 'bg-green-100 text-green-700' :
                'bg-purple-100 text-purple-700'
              }`}>{n}</span>
            ))}
          </div>
          {jour.poles && jour.poles.length > 0 && (
            <div className="flex gap-1">
              {jour.poles.map(p => (
                <span key={p} className={`px-2 py-0.5 rounded text-xs font-medium ${
                  p.toLowerCase().includes('poly') ? 'bg-amber-100 text-amber-800' :
                  p.toLowerCase().includes('com') ? 'bg-teal-100 text-teal-700' :
                  p.toLowerCase().includes('jur') ? 'bg-rose-100 text-rose-700' :
                  'bg-gray-100 text-gray-700'
                }`}>{p}</span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-sm font-medium">{jour.nombre_salles} salles</div>
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {jour.total_etudiants} / {jour.total_capacite} places
            </div>
          </div>
          <div className={`text-lg font-bold ${occupationColor}`}>
            {jour.taux_occupation_global}%
          </div>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>

      {/* Résumé effectifs par site */}
      {expanded && jour.par_site && jour.par_site.length > 0 && (
        <div className={`flex flex-wrap gap-3 px-4 pt-3 pb-1`}>
          {jour.par_site.map((site) => {
            const siteOcc = site.taux_occupation > 90 ? 'text-red-600' : site.taux_occupation > 70 ? 'text-orange-600' : 'text-green-600';
            return (
              <div key={site.site_nom} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${isDark ? 'bg-gray-750 border-gray-600' : 'bg-blue-50 border-blue-200'}`}>
                <Building2 size={14} className="text-blue-500" />
                <span className={`font-semibold text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{site.site_nom}</span>
                <span className={`text-sm font-bold ${siteOcc}`}>{site.effectif}</span>
                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>/ {site.capacite} ({site.nombre_salles} salles)</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Détail salles groupé par site */}
      {expanded && (
        <div className="p-4">
          <table className={`w-full border-collapse text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            <thead>
              <tr className={isDark ? 'bg-gray-700' : 'bg-gray-100'}>
                <th className="text-left px-3 py-2 font-medium">Salle</th>
                <th className="text-center px-3 py-2 font-medium">Capacité</th>
                <th className="text-center px-3 py-2 font-medium">Effectif</th>
                <th className="text-center px-3 py-2 font-medium">Taux</th>
                <th className="text-center px-3 py-2 font-medium">Statut</th>
                <th className="text-center px-3 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(sallesParSite).map(([siteNom, salles]) => {
                const siteCap = salles.reduce((s, r) => s + r.capacite, 0);
                const siteEff = salles.reduce((s, r) => s + r.effectif, 0);
                const siteTaux = siteCap > 0 ? Math.round(siteEff / siteCap * 1000) / 10 : 0;
                const siteColor = siteTaux > 90 ? 'text-red-500' : siteTaux > 70 ? 'text-orange-500' : 'text-green-500';

                return (
                  <React.Fragment key={siteNom}>
                    {/* En-tête site */}
                    <tr className={isDark ? 'bg-blue-900/20' : 'bg-blue-50'}>
                      <td colSpan={6} className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Building2 size={15} className="text-blue-500" />
                            <span className="font-bold text-sm">{siteNom}</span>
                            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              ({salles.length} salles)
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span><span className="font-medium">Effectif:</span> <span className="font-bold">{siteEff}</span></span>
                            <span><span className="font-medium">Capacité:</span> {siteCap}</span>
                            <span className={`font-bold ${siteColor}`}>{siteTaux}%</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                    {/* Salles du site */}
                    {salles.map((salle) => (
                      <tr
                        key={salle.salle_id}
                        className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} ${!salle.salle_actif ? 'opacity-50' : ''}`}
                      >
                        <td className={`px-3 py-2 pl-8 font-medium ${!salle.salle_actif ? 'line-through' : ''}`}>
                          {salle.salle_nom}
                        </td>
                        <td className="px-3 py-2 text-center">{salle.capacite}</td>
                        <td className="px-3 py-2 text-center font-medium">{salle.effectif}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`font-medium ${
                            salle.taux_occupation > 90 ? 'text-red-500' :
                            salle.taux_occupation > 70 ? 'text-orange-500' : 'text-green-500'
                          }`}>
                            {salle.taux_occupation}%
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            salle.salle_actif
                              ? (isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700')
                              : (isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700')
                          }`}>
                            {salle.salle_actif ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => onToggleSalle(salle.salle_id)}
                            className={`p-1 rounded transition ${isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                            title={salle.salle_actif ? 'Désactiver cette salle' : 'Réactiver cette salle'}
                          >
                            {salle.salle_actif
                              ? <ToggleRight size={20} className="text-green-500" />
                              : <ToggleLeft size={20} className="text-red-400" />
                            }
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className={`border-t-2 font-medium ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
                <td className="px-3 py-2">Total général</td>
                <td className="px-3 py-2 text-center">{jour.total_capacite}</td>
                <td className="px-3 py-2 text-center">{jour.total_etudiants}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`font-bold ${occupationColor}`}>{jour.taux_occupation_global}%</span>
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Vue globale
// ============================================================================

interface GlobalRecapProps {
  salles: RecapSalleGlobal[];
  isDark: boolean;
  onToggleSalle: (id: number) => void;
}

const GlobalRecap: React.FC<GlobalRecapProps> = ({ salles, isDark, onToggleSalle }) => {
  // Grouper par site
  const sites: Record<string, RecapSalleGlobal[]> = {};
  for (const s of salles) {
    if (!sites[s.site_nom]) sites[s.site_nom] = [];
    sites[s.site_nom].push(s);
  }

  return (
    <div className="space-y-4">
      {Object.entries(sites).map(([siteNom, sallesList]) => {
        const totalCapacite = sallesList.reduce((sum, s) => sum + s.capacite, 0);
        const totalEffectif = sallesList.reduce((sum, s) => sum + s.effectif, 0);

        return (
          <div key={siteNom} className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`flex items-center justify-between px-4 py-3 ${isDark ? 'bg-gray-750' : 'bg-blue-50'}`}>
              <div className="flex items-center gap-2">
                <Building2 size={18} className="text-blue-500" />
                <span className="font-bold">{siteNom}</span>
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {sallesList.length} salles · {totalEffectif} étudiants · {totalCapacite} places
              </div>
            </div>

            <table className={`w-full border-collapse text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
              <thead>
                <tr className={isDark ? 'bg-gray-700' : 'bg-gray-100'}>
                  <th className="text-left px-3 py-2 font-medium">Salle</th>
                  <th className="text-center px-3 py-2 font-medium">Capacité</th>
                  <th className="text-center px-3 py-2 font-medium">Effectif</th>
                  <th className="text-center px-3 py-2 font-medium">L1</th>
                  <th className="text-center px-3 py-2 font-medium">L2</th>
                  <th className="text-center px-3 py-2 font-medium">L3</th>
                  <th className="text-center px-3 py-2 font-medium">Statut</th>
                  <th className="text-center px-3 py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {sallesList.map((salle) => (
                  <tr
                    key={salle.salle_id}
                    className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} ${!salle.salle_actif ? 'opacity-50' : ''}`}
                  >
                    <td className={`px-3 py-2 font-medium ${!salle.salle_actif ? 'line-through' : ''}`}>
                      {salle.salle_nom}
                    </td>
                    <td className="px-3 py-2 text-center">{salle.capacite}</td>
                    <td className="px-3 py-2 text-center font-medium">{salle.effectif}</td>
                    <td className="px-3 py-2 text-center">{salle.par_niveau?.L1 || '-'}</td>
                    <td className="px-3 py-2 text-center">{salle.par_niveau?.L2 || '-'}</td>
                    <td className="px-3 py-2 text-center">{salle.par_niveau?.L3 || '-'}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        salle.salle_actif
                          ? (isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700')
                          : (isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700')
                      }`}>
                        {salle.salle_actif ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => onToggleSalle(salle.salle_id)}
                        className={`p-1 rounded transition ${isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                        title={salle.salle_actif ? 'Désactiver cette salle' : 'Réactiver cette salle'}
                      >
                        {salle.salle_actif
                          ? <ToggleRight size={20} className="text-green-500" />
                          : <ToggleLeft size={20} className="text-red-400" />
                        }
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className={`border-t-2 font-medium ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
                  <td className="px-3 py-2">Total</td>
                  <td className="px-3 py-2 text-center">{totalCapacite}</td>
                  <td className="px-3 py-2 text-center">{totalEffectif}</td>
                  <td className="px-3 py-2 text-center">
                    {sallesList.reduce((sum, s) => sum + (s.par_niveau?.L1 || 0), 0) || '-'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {sallesList.reduce((sum, s) => sum + (s.par_niveau?.L2 || 0), 0) || '-'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {sallesList.reduce((sum, s) => sum + (s.par_niveau?.L3 || 0), 0) || '-'}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })}
    </div>
  );
};

export default RecapSalles;
